import React, { useEffect } from 'react';
import { useSignal } from "@preact/signals-react";

interface MicrophoneVisualizerProps {
  isActive: boolean;
  isThinking?: boolean;
  size?: number;
  barCount?: number;
  barWidth?: number;
  barColor?: string;
  centerRadius?: number;
}

export function MicrophoneVisualizer({
  isActive,
  isThinking = false,
  size = 80,
  barCount = 32,
  barWidth = 3,
  barColor = '#1648a8',
  centerRadius = 15
}: MicrophoneVisualizerProps) {
  const local = {
    Canvas: useSignal<HTMLCanvasElement | null>(null),
    AudioContext: useSignal<AudioContext | null>(null),
    Analyser: useSignal<AnalyserNode | null>(null),
    MediaStream: useSignal<MediaStream | null>(null),
    AnimationFrame: useSignal<number>(0),
    IsInitialized: useSignal(false),
    CurrentBarCount: useSignal(barCount),
    CurrentBarWidth: useSignal(barWidth),
    CurrentMinHeight: useSignal(0),
    Indices: useSignal<number[]>([]),
    SwapTimeout: useSignal<NodeJS.Timeout | null>(null),
    PrevHeights: useSignal<number[]>([]),
    IsThinking: useSignal(isThinking)
  };

  useEffect(() => {
    // Initialize indices and previous heights
    local.Indices.value = Array.from({ length: barCount }, (_, i) =>
      Math.floor((i / barCount) * 128) // 128 is half of fftSize (256), used for frequency analysis
    );
    local.PrevHeights.value = Array(barCount).fill(0);

    if (isActive && !local.IsInitialized.value) {
      // Initialize dynamic bar proportion variables
      local.CurrentBarCount.value = barCount;
      local.CurrentBarWidth.value = barWidth;
      local.CurrentMinHeight.value = 0;
      console.log('effect fired on');
      initializeMicrophone();
      startSwappingIndices();
    }

    return cleanup;
  }, [isActive]);

  useEffect(() => {
    local.IsThinking.value = isThinking;
  }, [isThinking]);

  // Randomly swap indices
  const startSwappingIndices = () => {
    const swapIndices = () => {
      const arr = local.Indices.value;
      if (arr.length > 1) {
        const i = Math.floor(Math.random() * arr.length);
        let j = Math.floor(Math.random() * arr.length);
        while (j === i) j = Math.floor(Math.random() * arr.length);
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      const nextDelay = 50 + Math.random() * 200;
      local.SwapTimeout.value = setTimeout(swapIndices, nextDelay);
    };
    swapIndices();
  };

  const initializeMicrophone = async () => {
    const { log } = await import("../../library/logger");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      local.MediaStream.value = stream;

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      local.AudioContext.value = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      local.Analyser.value = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      local.IsInitialized.value = true;
      startVisualization();
    } catch (error) {
      log.error("Error accessing microphone:", error instanceof Error ? error.message : String(error));
    }
  };

  const startVisualization = () => {
    if (!local.Canvas.value || !local.Analyser.value) return;

    const ctx = local.Canvas.value.getContext('2d');
    if (!ctx) return;

    let bufferLength = local.Analyser.value.frequencyBinCount;
    let dataArray = new Uint8Array(bufferLength);
    const radius = size / 2;
    const centerX = radius;
    const centerY = radius;

    const animate = () => {
      if (!isActive) return;

      local.Analyser.value?.getByteFrequencyData(dataArray);

      // Discard lower frequencies to prevent background sounds from showing up
      const lowerCutoff = Math.floor(bufferLength * 0.03);
      dataArray = dataArray.slice(lowerCutoff);
      bufferLength = dataArray.length;

      ctx.clearRect(0, 0, size, size);

      // Animate bar proportions
      const targetBarCount = local.IsThinking.value ? 20 : barCount;
      const targetBarWidth = local.IsThinking.value ? barWidth * 2 : barWidth;
      const targetMinHeight = local.IsThinking.value ? barWidth * 2 : 0;
      if (Math.abs(local.CurrentBarCount.value - targetBarCount) > 0.1) {
        local.CurrentBarCount.value += (targetBarCount - local.CurrentBarCount.value) * 0.1;
      }
      if (Math.abs(local.CurrentBarWidth.value - targetBarWidth) > 0.1) {
        local.CurrentBarWidth.value += (targetBarWidth - local.CurrentBarWidth.value) * 0.1;
      }
      if (Math.abs(local.CurrentMinHeight.value - targetMinHeight) > 0.1) {
        local.CurrentMinHeight.value += (targetMinHeight - local.CurrentMinHeight.value) * 0.1;
      }

      // Calculate average volume for dynamic scaling
      const average = dataArray.reduce((sum, value) => sum + Math.pow(value / 255, 1.5), 0) / bufferLength;
      const volumeScale = Math.max(0.3, Math.min(1.5, average * 2));

      // Update indices and prevHeights if bar count changed
      const currentBarCount = Math.floor(local.CurrentBarCount.value);
      if (local.Indices.value.length !== currentBarCount) {
        local.Indices.value = Array.from({ length: currentBarCount }, (_, i) =>
          Math.floor((i / currentBarCount) * 128)
        );
        local.PrevHeights.value = Array(currentBarCount).fill(0);
      }

      // Draw bars with smoothed heights and swapped indices
      const prevHeights = local.PrevHeights.value;
      for (let i = 0; i < currentBarCount; i++) {
        const angle = (i / currentBarCount) * Math.PI * 2;
        // Adjust dataIndex to match new bufferLength
        const dataIndex = Math.floor((i / currentBarCount) * bufferLength);
        const rawValue = dataArray[dataIndex] / 255;
        const mappedValue = Math.pow(rawValue, 1.5);
        const targetHeight = Math.max(
          local.CurrentMinHeight.value,
          rawValue < 0.5 ? 0 : mappedValue * (radius - centerRadius) * volumeScale
        );

        // Smooth transition
        const prevHeight = prevHeights[i];
        const smoothHeight = prevHeight + (targetHeight - prevHeight) * 0.25;
        prevHeights[i] = smoothHeight;

        const startX = centerX + Math.cos(angle) * centerRadius;
        const startY = centerY + Math.sin(angle) * centerRadius;
        const endX = centerX + Math.cos(angle) * (centerRadius + smoothHeight);
        const endY = centerY + Math.sin(angle) * (centerRadius + smoothHeight);

        ctx.strokeStyle = barColor;
        ctx.lineWidth = local.CurrentBarWidth.value;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }

      local.AnimationFrame.value = requestAnimationFrame(animate);
    };

    animate();
  };

  const cleanup = () => {
    if (local.AnimationFrame.value) {
      cancelAnimationFrame(local.AnimationFrame.value);
      local.AnimationFrame.value = 0;
    }
    if (local.SwapTimeout.value) {
      clearTimeout(local.SwapTimeout.value);
      local.SwapTimeout.value = null;
    }
    if (local.MediaStream.value) {
      local.MediaStream.value.getTracks().forEach(track => track.stop());
      local.MediaStream.value = null;
    }

    if (local.AudioContext.value) {
      local.AudioContext.value.close();
      local.AudioContext.value = null;
    }

    local.Analyser.value = null;
    local.IsInitialized.value = false;
  };

  if (!isActive) {
    return null;
  }

  return (
    <div
      className={`mic-visualizer-wrapper${local.IsThinking.value ? ' thinking' : ''}`}
      style={{ width: size, height: size }}
    >
      <canvas
        ref={el => (local.Canvas.value = el)}
        width={size}
        height={size}
        className={`mic-visualizer-canvas${local.IsThinking.value ? ' spinning' : ''}`}
        style={{
          width: size,
          height: size,
        }}
      />
    </div>
  );
}
